import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import { LayoutUtilsService, QueryParamsModel } from '../../../../../../core/_base/crud';
import {
  DrugGroup,
  DrugGroupOnServerDeleted,
  DrugGroupOnServerUpdated,
  DrugGroupsDataSource,
  DrugGroupsPageRequested,
  ViewContext,
} from '../../../../../../core/lists';
import { TranslateService } from '@ngx-translate/core';
import { Update } from '@ngrx/entity';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { TabletUtil } from '../../../../../../core/utils';

@Component({
  selector: 'kt-drug-groups-list',
  templateUrl: './drug-groups-list.component.html',
  styleUrls: ['./drug-groups-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrugGroupsListComponent implements OnInit, OnDestroy {
  dataSource: DrugGroupsDataSource;
  displayedColumns = ['name', 'isActive', 'actions'];
  searchBarColumns = ['searchName', 'searchIsActive', 'searchActions'];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchNameInput = '';
  searchIsActiveSelect = 'true';
  statusTracker = 0;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;

  constructor(
    private navigationService: NavigationService,
    private permissionsService: NgxPermissionsService,
    private tabletUtil: TabletUtil,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadDrugGroupsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadDrugGroupsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new DrugGroupsDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
      status: ['true'],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadDrugGroupsList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadDrugGroupsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'name',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new DrugGroupsPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    filter.isActive = this.searchIsActiveSelect;
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      filter.isActive = appliedFilters.isActive;
      this.searchIsActiveSelect = appliedFilters.isActive;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchNameInput = appliedFilters.name;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'name';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.name = this.searchNameInput;
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'name';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  deleteDrugGroup(_item: DrugGroup) {
    const _title: string = this.translate.instant('DRUG_GROUP.DELETE_DRUG_GROUP_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'DRUG_GROUP.DELETE_DRUG_GROUP_SIMPLE.DESCRIPTION',
    );
    const _waitDescription: string = this.translate.instant(
      'DRUG_GROUP.DELETE_DRUG_GROUP_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new DrugGroupOnServerDeleted({ id: _item.id }));
    });
  }

  updateStatusForDrugGroup(drugGroup: DrugGroup) {
    const _title: string = this.translate.instant('DRUG_GROUP.UPDATE_STATUS_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'DRUG_GROUP.UPDATE_STATUS_SIMPLE.DESCRIPTION',
    );
    const _waitDescription: string = this.translate.instant(
      'DRUG_GROUP.UPDATE_STATUS_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const newDrugGroup: DrugGroup = Object.assign(new DrugGroup(), drugGroup);
      newDrugGroup.changeActiveStatus();
      const updateDrugGroup: Update<DrugGroup> = {
        id: newDrugGroup.id,
        changes: newDrugGroup,
      };
      this.store.dispatch(
        new DrugGroupOnServerUpdated({
          partialDrugGroup: updateDrugGroup,
          drugGroup: newDrugGroup,
        }),
      );
    });
  }

  canViewDrugGroup(): boolean {
    return !!this.permissionsService.getPermission('canViewDrugGroup');
  }

  canEditDrugGroup(): boolean {
    return !!this.permissionsService.getPermission('canEditDrugGroup');
  }

  createDrugGroup() {
    this.navigationService.navigateTo('lists/drug-groups/add');
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewDrugGroup(drugGroup: DrugGroup) {
    if (!this.canViewDrugGroup()) return;

    if (this.canEditDrugGroup()) {
      this.editDrugGroup(drugGroup.id);
      return;
    }
    const queryParams = {
      viewContext: ViewContext.drugGroups,
    };
    this.navigationService.navigateTo('lists/drug-groups/view/' + drugGroup.id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewDrugGroupDblclick(ref: string, drugGroup: DrugGroup): void {
    this.tabletUtil.handleDblclick(ref, this.viewDrugGroup.bind(this, drugGroup));
  }

  editDrugGroup(id: number) {
    const queryParams = {
      viewContext: ViewContext.drugGroups,
    };

    this.navigationService.navigateTo('lists/drug-groups/edit/' + id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  reloadDrugGroupsList(): void {
    this.paginator.pageIndex = 0;
    this.loadDrugGroupsList();
  }

  
  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect = 'true';
        break;
      case 1:
        this.searchIsActiveSelect = 'false';
        break;
      case 2:
        this.searchIsActiveSelect = '';
        break;
    }

    this.reloadDrugGroupsList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchNameInput = '';
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadDrugGroupsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadDrugGroupsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadDrugGroupsList();
  }
}
